/**
 * Double bed designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_home_designs`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const doubleBedDesignsPack: ToilePack = {
    slug: 'double-bed-designs',
    category: 'home',
    name: 'Double bed designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'storage', label: 'Storage', subject: 'a double bed with hydraulic storage and a padded headboard'},
        {key: 'upholstered', label: 'Upholstered', subject: 'a bed with a tall upholstered headboard'},
        {key: 'wood', label: 'Wooden', subject: 'a sheesham wood double bed'},
    ],
    styles: [
        {key: 'modern', label: 'Modern', style: 'modern master bedroom with a panelled wall, side tables and soft linen, soft morning daylight from camera-left, a bright balance with gentle shadows', palette: 'warm white, beige and light oak', framing: 'eye-level front view, the bed centred'},
        {key: 'royal', label: 'Royal', style: 'royal Indian bedroom with carved trim, silk cushions and a jharokha window, warm afternoon sun from camera-right, a rich balance of sunlit fabric and shade', palette: 'maroon, gold, ivory and dark wood', framing: 'three-quarter view from the foot of the bed'},
        {key: 'evening', label: 'Evening', style: 'cosy evening bedroom with a warm cove glow from overhead and dusk blue at a window, a moody balance of warm glow and soft shadow', palette: 'deep blue, walnut and warm amber', framing: 'low side view at mattress height'},
    ],
    directions: ['kids room', 'king size', 'grey bedroom'],
    directionHint: 'try: kids room',
    seo: {
        title: 'Double bed designs: storage, upholstered and wooden',
        metaDescription: 'Nine double bed pictures: storage, upholstered and wooden beds, each in a modern, royal or evening bedroom.',
        h1: 'Double bed designs: storage, upholstered and wooden',
        tagline: 'Nine pictures, three bed frames, three bedrooms each: modern, royal and evening.',
        intro: 'Three double bed frames to brief a carpenter on: a bed with hydraulic storage and a padded headboard, a bed with a tall upholstered headboard, and a sheesham wood bed. Each is shown in a modern master bedroom, in a royal Indian bedroom with carved trim, and in an evening bedroom with warm light. Type a different room, such as a grey bedroom, for a variation on the same three frames.',
        useCases: [
            'Briefing a carpenter on a double bed frame',
            'A storage bed for a small bedroom',
            'A sheesham wood bed for a traditional room',
            'An upholstered headboard for a master bedroom',
        ],
        faq: [
            {q: 'Do the pictures show the bed size?', a: 'No. They show the look of each bed, with no dimensions. Measure your room and the mattress before you order.'},
            {q: 'What is the difference between the three bedrooms?', a: 'The modern room has a panelled wall and soft linen. The royal room has carved trim, silk cushions and a jharokha window. The evening room has a warm cove glow and dusk blue at the window.'},
            {q: 'Are these real beds?', a: 'No. They are generated images styled to look like photographs of Indian bedrooms.'},
            {q: 'Can I ask for a king size bed?', a: 'Yes. Type it as a direction. The result is a variation on the same three frames, not a guaranteed match.'},
        ],
    },
}
